import React from 'react';
import { useSelector } from 'react-redux';
import { useSettings } from '@wix/tpa-settings/react';
import { Trans, useTranslation } from '@wix/yoshi-flow-editor';
import { UISref, useRouter } from '@wix/tpa-router/react';

import {
  selectAppData,
  selectFeedItems,
  selectFeedStatuses,
  selectGroup,
} from 'store/selectors';

import { useDidUpdate } from 'common/hooks';
import { useController } from 'common/context/controller';
import { Box } from 'wui/Box';
import { Button } from 'wui/Button';
import { EmptyState } from 'wui/EmptyState';
import { ErrorState } from 'wui/ErrorState';

import { FeedItem, FeedItemSkeleton } from 'common/components/FeedItem';
import settingsParams from 'FeedWidget/settingsParams';

import classes from './Feed.scss';

export function Feed() {
  const settings = useSettings();
  const { t } = useTranslation();
  const router = useRouter();

  const items = useSelector(selectFeedItems);
  const { feed$ } = useController();
  const { groupId } = useSelector(selectAppData);
  const group = useSelector(selectGroup(groupId));
  const statuses = useSelector(selectFeedStatuses);

  const postsCount = settings.get(settingsParams.postsCount);

  useDidUpdate(() => {
    feed$.fetch(groupId, {
      limit: postsCount,
    });
  }, [groupId, group.membership, postsCount]);

  if (statuses.fetch.pending) {
    return (
      <>
        <FeedItemSkeleton />
        <FeedItemSkeleton />
        <FeedItemSkeleton />
      </>
    );
  }

  if (statuses.fetch.error) {
    return (
      <ErrorState
        title={t('groups-web.discussion.error-state.title')}
        subtitle={
          <Trans
            i18nKey={t('groups-web.discussion.error-state.owner.message')}
            components={[
              <span key="0">Try reloading</span>,
              <br key="1" />,
              <span key="2">contact</span>,
            ]}
          />
        }
        actionLabel={t('groups-web.discussion.error-state.cta')}
        onRetry={() =>
          feed$.fetch(groupId, {
            limit: postsCount,
          })
        }
      />
    );
  }

  if (!items.length) {
    return (
      <EmptyState
        title={t('groups-web.discussion.first.title')}
        subtitle={t('groups-web.discussion.first.description')}
        action={
          <Button onClick={redirectToGroupPage}>
            {t('groups-web.discussion.first.button')}
          </Button>
        }
        variant="section"
      />
    );
  }

  return (
    <Box className={classes.feed} direction="vertical">
      {items.map((item) => (
        <FeedItem
          promote
          truncate
          key={item.feedItemId}
          item={{
            ...item,
            permissions: {
              ...item.permissions,
              canUpdate: false,
              canDelete: false,
            },
          }}
        />
      ))}

      <Box align="center">
        <UISref state="group" params={{ slug: group.slug }}>
          <Button as="a">{t('groups-web.feed.show-more.label')}</Button>
        </UISref>
      </Box>
    </Box>
  );

  function redirectToGroupPage() {
    router.go('group', { slug: group.slug });
  }
}

Feed.displayName = 'Feed';
